import React, { useState, useEffect, useRef } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { io } from 'socket.io-client';
import { Send, Paperclip, ArrowLeft, MoreVertical, Check, CheckCheck, Search, X, Maximize2 } from 'lucide-react';

const SOCKET_URL = 'http://localhost:2026';
const socket = io(SOCKET_URL);

// --- DATE FORMATTER HELPER ---
// Ye function MySQL date string aur ISO string dono ko handle karega
const formatTime = (dateString: string) => {
    if (!dateString) return '';
    try {
        const date = new Date(dateString);
        // Agar date invalid hai (kabhi kabhi MySQL format issue ki wajah se)
        if (isNaN(date.getTime())) {
            // Safari/Mobile support ke liye space ko 'T' se replace krte hain
            const safeDate = new Date(dateString.replace(' ', 'T'));
            if (!isNaN(safeDate.getTime())) {
                return safeDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
            }
            return '';
        }
        return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } catch (e) {
        return '';
    }
};

const ChatPage: React.FC = () => {
    const { conversationId } = useParams();
    const location = useLocation();
    const navigate = useNavigate();
    const queryParams = new URLSearchParams(location.search);
    
    const paramProductName = queryParams.get('productName');
    const paramName = queryParams.get('companyName') || queryParams.get('buyerName') || 'User';

    const [conversations, setConversations] = useState<any[]>([]); 
    const [messages, setMessages] = useState<any[]>([]);
    const [newMessage, setNewMessage] = useState('');
    const [uploading, setUploading] = useState(false);
    const [previewImage, setPreviewImage] = useState<string | null>(null);
    
    const chatContainerRef = useRef<HTMLDivElement>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const user = JSON.parse(localStorage.getItem('currentUser') || '{}'); 
    const isSeller = user.userType === 'seller';
    const receiverId = isSeller ? queryParams.get('buyerId') : queryParams.get('sellerId');

    // Sidebar Logic: Fetching Chat List
    useEffect(() => {
        const fetchList = async () => {
            try {
                const res = await fetch(`${SOCKET_URL}/api/chat/my-conversations?userId=${user.id}&userType=${user.userType}`);
                const data = await res.json();
                setConversations(Array.isArray(data) ? data : []);
            } catch (err) { 
                console.error("Error fetching chat list", err); 
            }
        };
        if (user.id) fetchList();
        
        // Listen for sidebar updates (e.g. new msg moves chat to top)
        const interval = setInterval(() => {
             if (user.id) fetchList();
        }, 5000); // Poll every 5s for updated list order/time

        return () => clearInterval(interval);
    }, [user.id, user.userType, messages]); // Refresh when messages change

    // Active Chat Logic
    useEffect(() => {
        if (user.id) socket.emit('join_room', { userId: user.id, userType: user.userType });

        if (conversationId) {
            fetch(`${SOCKET_URL}/api/chat/messages/${conversationId}`)
                .then(res => res.json())
                .then(data => {
                    setMessages(Array.isArray(data) ? data : []);
                    markMessagesAsRead(); 
                });
        }

        socket.on('receive_message', (data) => {
            if (data.conversation_id == conversationId) {
                setMessages((prev) => [...prev, data]);
                markMessagesAsRead();
            }
        });

        return () => { socket.off('receive_message'); };
    }, [conversationId, user.id]);

    useEffect(() => {
        if (chatContainerRef.current) {
            chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
        }
    }, [messages]);

    const markMessagesAsRead = () => {
        if(conversationId && user.id && receiverId) {
            socket.emit('mark_messages_read', { conversationId, readerId: user.id, readerType: user.userType, senderId: receiverId });
        }
    };

    const sendMessage = (e?: any, attachmentUrl?: string, attachmentType?: string) => {
        if (e) e.preventDefault();
        if (!newMessage.trim() && !attachmentUrl) return;

        const messageData = {
            conversationId, 
            senderId: user.id, 
            senderType: isSeller ? 'seller' : 'buyer',
            receiverId, 
            messageText: newMessage, 
            attachmentUrl: attachmentUrl || null, 
            attachmentType: attachmentType || null,
            // Optimistic Update: Add current time immediately
            created_at: new Date().toISOString() 
        };

        socket.emit('send_message', messageData);
        setMessages((prev) => [...prev, { ...messageData, is_read: 0 }]);
        setNewMessage('');
    };

    const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files[0]) {
            setUploading(true);
            const formData = new FormData();
            formData.append('file', e.target.files[0]);
            try {
                const res = await fetch(`${SOCKET_URL}/api/chat/upload`, { method: 'POST', body: formData });
                const data = await res.json();
                if (data.fileUrl) sendMessage(null, data.fileUrl, data.fileType);
            } catch (err) { alert("Upload failed"); } 
            finally { setUploading(false); if(fileInputRef.current) fileInputRef.current.value = ""; }
        }
    };

    return (
        <div className="flex h-[90vh] md:h-[80vh] bg-[#f0f2f5] overflow-hidden m-0 md:m-4 md:rounded-lg shadow-2xl border border-gray-200">
            
            {/* Image Preview Modal */}
            {previewImage && (
                <div className="fixed inset-0 z-[1000] bg-black/90 flex items-center justify-center p-4 backdrop-blur-sm">
                    <button onClick={() => setPreviewImage(null)} className="absolute top-5 right-5 text-white bg-white/20 p-2 rounded-full transition">
                        <X size={32} />
                    </button>
                    <img src={previewImage} className="max-w-full max-h-full object-contain rounded shadow-2xl" alt="preview" />
                </div>
            )}

            {/* --- LEFT SIDEBAR: CHAT LIST --- */}
            <div className={`w-full md:w-80 bg-white border-r flex flex-col ${conversationId ? 'hidden md:flex' : 'flex'}`}>
                <div className="p-3.5 bg-[#f0f2f5] flex items-center justify-between border-b">
                    <div className="w-10 h-10 bg-teal-600 rounded-full flex items-center justify-center text-white font-bold uppercase shadow-sm">
                        {user.name?.charAt(0) || 'U'}
                    </div>
                    <div className="flex gap-4 text-gray-500"><MoreVertical size={20} className="cursor-pointer" /></div>
                </div>
                <div className="p-3 bg-white border-b">
                    <div className="flex items-center gap-3 bg-[#f0f2f5] px-4 py-1.5 rounded-full border border-gray-100">
                        <Search size={16} className="text-gray-400" />
                        <input type="text" placeholder="Search chats" className="bg-transparent border-none outline-none text-sm w-full py-1" />
                    </div>
                </div>

                <div className="flex-1 overflow-y-auto">
                    {conversations.length > 0 ? (
                        conversations.map((chat) => {
                            const chatName = isSeller ? (chat.buyer_name || chat.otherPartyName || 'Buyer') : (chat.company_name || chat.otherPartyName || 'Seller');
                            return (
                                <div key={chat.conversationId} 
                                     onClick={() => navigate(`/chat/${chat.conversationId}?companyName=${chatName}&productName=${chat.productName}&${isSeller ? `buyerId=${chat.otherPartyId}` : `sellerId=${chat.otherPartyId}`}`)}
                                     className={`flex items-center gap-3 p-4 border-b cursor-pointer hover:bg-gray-50 transition-all ${conversationId == chat.conversationId ? 'bg-[#f0f2f5]' : ''}`}>
                                    <div className="w-12 h-12 bg-teal-700 rounded-full flex items-center justify-center text-white font-bold shadow-inner uppercase">
                                        {chatName.charAt(0)}
                                    </div>
                                    <div className="flex-1 overflow-hidden">
                                        <div className="flex justify-between items-center mb-0.5">
                                            <h4 className="font-bold text-sm text-gray-800 truncate">{chatName}</h4>
                                            {/* ADDED: Sidebar Time */}
                                            <span className="text-[10px] text-gray-400">{formatTime(chat.updated_at)}</span>
                                        </div>
                                        <p className="text-xs text-gray-500 truncate">{chat.lastMessage || 'Media message'}</p>
                                    </div>
                                </div>
                            );
                        })
                    ) : (
                        <div className="p-10 text-center text-gray-400 text-sm">No chats found.</div>
                    )}
                </div>
            </div>

            {/* --- RIGHT PANEL: ACTIVE CHAT --- */}
            <div className={`flex-1 flex flex-col bg-[#efeae2] relative ${!conversationId ? 'hidden md:flex' : 'flex'}`}>
                {conversationId ? (
                    <>
                        {/* Header Fixed */}
                        <div className="p-3 bg-[#f0f2f5] border-b flex items-center justify-between sticky top-0 z-50 shadow-sm">
                            <div className="flex items-center gap-3">
                                <ArrowLeft className="md:hidden cursor-pointer text-gray-600" onClick={() => navigate('/chat')} />
                                <div className="w-10 h-10 bg-teal-700 rounded-full flex items-center justify-center text-white font-bold uppercase shadow-sm">
                                    {paramName.charAt(0).toUpperCase()}
                                </div>
                                <div>
                                    <h3 className="font-bold text-gray-800 text-sm leading-tight">{paramName}</h3>
                                    <p className="text-[10px] text-teal-700 font-bold truncate max-w-[150px]">{paramProductName}</p>
                                </div>
                            </div>
                            <MoreVertical size={20} className="text-gray-400 cursor-pointer" />
                        </div>

                        {/* Messages Area */}
                        <div ref={chatContainerRef} className="flex-1 overflow-y-auto p-4 md:px-12 space-y-2 bg-[url('https://user-images.githubusercontent.com/15075759/28719144-86dc0f70-73b1-11e7-911d-60d70fcded21.png')] bg-repeat">
                            {messages.map((msg, idx) => {
                                const isMe = (msg.sender_id === user.id) || (msg.senderId === user.id);
                                return (
                                    <div key={idx} className={`flex ${isMe ? 'justify-end' : 'justify-start'} animate-fade-in`}>
                                        <div className={`relative max-w-[85%] md:max-w-[70%] px-3 py-1.5 rounded-lg shadow-sm text-sm ${
                                            isMe ? 'bg-[#d9fdd3] text-gray-900 rounded-tr-none' : 'bg-white text-gray-800 rounded-tl-none'
                                        }`}>
                                            {(msg.attachment_url || msg.attachmentUrl) && (
                                                <div className="relative group cursor-pointer mb-1.5" onClick={() => setPreviewImage(msg.attachment_url || msg.attachmentUrl)}>
                                                    <img src={msg.attachment_url || msg.attachmentUrl} className="rounded border max-w-full h-auto" alt="attachment" />
                                                    <div className="absolute inset-0 bg-black/10 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-all">
                                                        <Maximize2 size={20} className="text-white" />
                                                    </div>
                                                </div>
                                            )}
                                            <p className="inline-block pb-2.5 pr-10 leading-snug whitespace-pre-wrap">{msg.message_text || msg.messageText}</p>
                                            <div className="absolute bottom-1 right-2 flex items-center gap-1">
                                                {/* FIXED: Time Display */}
                                                <span className="text-[9px] text-gray-400">
                                                    {formatTime(msg.created_at || msg.createdAt)}
                                                </span>
                                                {isMe && (msg.is_read ? <CheckCheck size={14} className="text-[#53bdeb]" /> : <Check size={14} className="text-gray-400" />)}
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>

                        {/* Input Bar Fixed at Bottom - Improved for Mobile */}
                        <div className="p-2 md:p-3 bg-[#f0f2f5] border-t flex items-center gap-2 md:gap-3 sticky bottom-0 w-full">
                            <input type="file" ref={fileInputRef} onChange={handleFileSelect} className="hidden" accept="image/*" />
                            <button onClick={() => fileInputRef.current?.click()} className="text-gray-500 hover:text-teal-600 transition p-1">
                                <Paperclip size={24} />
                            </button>
                            <div className="flex-1 bg-white rounded-full px-4 md:px-5 py-2 md:py-2.5 shadow-sm">
                                <input type="text" value={newMessage} 
                                       onChange={(e) => setNewMessage(e.target.value)} 
                                       onKeyDown={(e) => e.key === 'Enter' && sendMessage()} 
                                       placeholder="Type a message" className="w-full bg-transparent border-none outline-none text-sm" />
                            </div>
                            <button onClick={() => sendMessage()} disabled={uploading} className="bg-teal-700 text-white p-2.5 md:p-3 rounded-full shadow-lg hover:bg-teal-800 transition active:scale-95 disabled:bg-gray-400">
                                <Send size={20} />
                            </button>
                        </div>
                    </>
                ) : (
                    <div className="flex-1 flex flex-col items-center justify-center text-center p-10 bg-[#f8f9fa]">
                        <div className="w-24 h-24 bg-gray-200 rounded-full flex items-center justify-center mb-6">
                            <Search size={48} className="text-gray-400" />
                        </div>
                        <h2 className="text-3xl font-light text-gray-600 mb-2 tracking-tight">TradyFrog Web</h2>
                        <p className="text-sm text-gray-400 max-w-sm">Sidebar se chat select karein ya naya enquiry start karein.</p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ChatPage;